import { generateOpenApi } from '@ts-rest/open-api';
import type { OpenAPIObject, ResponseObject } from 'openapi3-ts';
import { apiSpec } from './api';
import type { ApiContractMetadata } from './models';
import { RATE_LIMITS } from './models';

type PartialOpenApi = Omit<OpenAPIObject, 'paths'>;

export const openApiDefinition = {
  openapi: '3.0.3',
  info: {
    title: 'Timely Integration Cloud API',
    version: '1.0',
    description:
      'Overview of next-gen, `ts-rest` driven NextJS Serverless API endpoints on Timely Integration Cloud',
    contact: {
      name: 'Timm Stokke',
      email: 'timm@memory.ai',
    },
    license: {
      name: 'Proprietary',
      url: 'https://timelyapp.com/terms',
    },
  },
  tags: [
    { name: 'accounts', description: 'Account management operations' },
    { name: 'admin', description: 'Administrative operations for system management' },
    { name: 'agent', description: 'AI agent operations' },
    { name: 'ai', description: 'AI and machine learning features' },
    { name: 'connections', description: 'Integration connection management' },
    { name: 'dispatch', description: 'Admin dispatch and job management' },
    { name: 'engine', description: 'AI engine webhooks and callbacks' },
    { name: 'health', description: 'Health check endpoints' },
    { name: 'integrations', description: 'Integration provider management' },
    { name: 'members', description: 'Account member management' },
    { name: 'memories', description: 'Memory and activity tracking' },
    { name: 'notes', description: 'AI notes and annotations' },
    { name: 'notifications', description: 'User notification management' },
    { name: 'predictions', description: 'AI predictions and suggestions' },
    { name: 'preferences', description: 'AI preference settings' },
    { name: 'pusher', description: 'Real-time event authentication' },
    { name: 'resources', description: 'External resource synchronization' },
    { name: 'sync', description: 'Data synchronization operations' },
    { name: 'timeEntryEvents', description: 'Time entry event tracking' },
    { name: 'timely', description: 'Timely-specific integrations' },
    { name: 'timesheetProposals', description: 'Timesheet proposal management' },
    { name: 'tools', description: 'Tool and application management' },
    { name: 'trackers', description: 'Memory tracker configuration' },
    { name: 'users', description: 'User management operations' },
  ],
  servers: [
    {
      url: 'http://localhost:3011/api',
      description: 'Local dev server',
    },
    {
      url: 'https://integrations.app.timelyapp.com/api',
      description: 'Production',
    },
    {
      url: 'https://integrations.app.betimely.com/api',
      description: 'Staging',
    },
  ],
  security: [
    {
      CookieAuth: [],
      BearerAuth: [],
      JWTAuth: [],
    },
  ],
  components: {
    securitySchemes: {
      BearerAuth: {
        type: 'http',
        scheme: 'bearer',
        bearerFormat: 'JWT',
        description: 'Supabase Auth specific',
      },
      JWTAuth: {
        type: 'apiKey',
        in: 'header',
        name: 'X-TIC-JWT',
        description: 'The hand-rolled JWT token to authenticate the user.',
      },
      CookieAuth: {
        type: 'apiKey',
        in: 'cookie',
        name: 'tic-session',
        description:
          'The JWT token provided in the `tic-sessions` cookie to authenticate the user.',
      },
    },
  },
} satisfies PartialOpenApi;

const hasMetadataProperty = (
  prop: keyof ApiContractMetadata,
  metadata: ApiContractMetadata
): metadata is ApiContractMetadata => {
  return !!metadata && typeof metadata === 'object' && prop in metadata;
};

/**
 * Generate the OpenAPI spec
 * @link https://ts-rest.com/docs/open-api
 */
export const openApiDocument = generateOpenApi(apiSpec, openApiDefinition, {
  setOperationId: true,
  /**
   * Modify the OpenAPI definitions before write.
   * See `metadata` & `ApiContractMetadataSchema`
   */
  operationMapper: (operation, appRoute) => {
    const metadata = (appRoute.metadata || {}) as ApiContractMetadata;
    const mappedOperation = { ...operation };

    if (hasMetadataProperty('openApiTags', metadata)) {
      mappedOperation.tags = metadata.openApiTags;
    }

    if (hasMetadataProperty('openApiSecurity', metadata)) {
      mappedOperation.security = metadata.openApiSecurity;
    }

    // Custom MCP extensions (local dev tooling only; not part of public API docs/contracts).
    if (hasMetadataProperty('mcp', metadata)) {
      (mappedOperation as Record<string, unknown>)['x-mcp'] = metadata.mcp;
    }

    // Contracts with `metadata.rateLimitPreset` will have rate limit headers added to the 200 response
    if (hasMetadataProperty('rateLimitPreset', metadata)) {
      const preset = !!metadata.rateLimitPreset && RATE_LIMITS[metadata.rateLimitPreset];
      const res200: ResponseObject | undefined = mappedOperation.responses[200];

      // If we have a preset, add rate limit headers to the 200 response
      if (preset && res200) {
        mappedOperation.responses[200].headers = res200?.headers || {};
        const append = '\n\n(Rate limit preset: ' + metadata.rateLimitPreset + ')';
        mappedOperation.description = (mappedOperation.description || '') + append;

        mappedOperation.responses[200].headers['x-ratelimit-preset'] = {
          description: `Rate limited for preset ${metadata.rateLimitPreset} is ${preset.numRequests}req/per ${preset.windowSize}`,
          required: false,
          schema: { type: 'string', default: metadata.rateLimitPreset },
        };
        mappedOperation.responses[200].headers['x-ratelimit-limit'] = {
          description: `The limit allotted in the window`,
          example: '200',
          required: false,
          schema: { type: 'string', default: String(preset.numRequests) },
        };
        mappedOperation.responses[200].headers['x-ratelimit-remaining'] = {
          description: `The remaining tokens in the window`,
          required: false,
          example: '120',
          schema: { type: 'string' },
        };
        mappedOperation.responses[200].headers['x-ratelimit-reset'] = {
          description: `When does the rate limit window reset`,
          required: false,
          example: '1725150600000',
          schema: { type: 'string' },
        };
      }
    }

    return mappedOperation;
  },
});

export default openApiDocument;
